<template>
  <Teleport to="body">
    <Transition name="login-overlay">
      <div 
        v-if="isOpen" 
        class="login-overlay-backdrop fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
        @click="close"
      />
    </Transition>
    
    <Transition name="login-modal">
      <div 
        v-if="isOpen" 
        class="login-modal-box fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[420px] max-h-[calc(100vh-2rem)] overflow-y-auto z-50"
        @click.stop
      >
        <!-- Close Button -->
        <button 
          type="button"
          @click="close"
          class="login-close-btn absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all z-[100] cursor-pointer"
          aria-label="Close"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Logo -->
        <div class="login-header pt-10 pb-6 flex justify-center">
          <img 
            src="/logos/icon-supplem.PNG" 
            alt="SupplemEarth" 
            class="h-20 w-auto object-contain"
          />
        </div>

        <!-- Form Section -->
        <div class="login-body px-6 sm:px-8 pb-8">
          <!-- Tabs -->
          <div class="login-tabs flex gap-1 mb-6 p-1 rounded-xl bg-gray-100/80">
            <button
              @click="activeTab = 'login'"
              :class="['login-tab flex-1 py-3 px-4 rounded-lg text-sm font-semibold tracking-tight transition-all duration-200 cursor-pointer', activeTab === 'login' ? 'login-tab-active' : 'login-tab-inactive']"
            >
              Sign In
            </button>
            <button
              @click="activeTab = 'register'"
              :class="['login-tab flex-1 py-3 px-4 rounded-lg text-sm font-semibold tracking-tight transition-all duration-200 cursor-pointer', activeTab === 'register' ? 'login-tab-active' : 'login-tab-inactive']"
            >
              Sign Up
            </button>
          </div>

          <!-- Login Form -->
          <form v-if="activeTab === 'login'" @submit.prevent="handleLogin" class="space-y-5">
            <div>
              <label class="login-label">Email Address</label>
              <input
                type="email"
                v-model="loginEmail"
                @blur="validateLoginEmail"
                @input="clearLoginError('email')"
                required
                placeholder="you@example.com"
                :class="['login-input w-full', loginErrors.email ? 'login-input-error' : '']"
              >
              <p v-if="loginErrors.email" class="login-error-text">{{ loginErrors.email }}</p>
            </div>

            <div>
              <label class="login-label">Password</label>
              <div class="relative">
                <input
                  :type="showLoginPassword ? 'text' : 'password'"
                  v-model="loginPassword"
                  @blur="validateLoginPassword"
                  @input="clearLoginError('password')"
                  required
                  placeholder="••••••••"
                  :class="['login-input w-full pr-12', loginErrors.password ? 'login-input-error' : '']"
                >
                <button
                  type="button"
                  @click="showLoginPassword = !showLoginPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  aria-label="Toggle password visibility"
                >
                  <svg v-if="showLoginPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <p v-if="loginErrors.password" class="login-error-text">{{ loginErrors.password }}</p>
            </div>

            <div class="flex flex-wrap items-center justify-between gap-2 pt-2 text-sm">
              <label class="flex items-center cursor-pointer group">
                <input type="checkbox" v-model="rememberMe" class="login-checkbox rounded border-gray-300 cursor-pointer flex-shrink-0">
                <span class="ml-2 text-gray-600 group-hover:text-gray-800 font-medium">Remember me</span>
              </label>
              <button
                type="button"
                id="forgot-password-link"
                @click="goToForgotPassword"
                class="login-link font-semibold cursor-pointer hover:underline transition-colors bg-transparent border-none p-0"
              >Forgot password?</button>
            </div>

            <button
              type="submit"
              :disabled="isLoggingIn"
              class="login-submit-btn w-full py-3.5 text-sm font-semibold rounded-xl mt-6 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span v-if="isLoggingIn" class="flex items-center justify-center gap-2">
                <svg class="animate-spin h-5 w-5 text-[#1a1200]" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
              <span v-else>Sign In</span>
            </button>
            
            <div v-if="loginError" class="login-message login-message-error mt-5">
              <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-sm text-red-800 font-medium">{{ loginError }}</p>
            </div>
          </form>

          <!-- Register Form -->
          <form v-else @submit.prevent="handleRegister" class="space-y-5">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="login-label">First Name <span class="text-red-500">*</span></label>
                <input
                  type="text"
                  v-model="registerFirstName"
                  @blur="validateRegisterFirstName"
                  @input="clearRegisterError('firstName')"
                  required
                  placeholder="John"
                  :class="['login-input w-full', registerErrors.firstName ? 'login-input-error' : '']"
                >
                <p v-if="registerErrors.firstName" class="login-error-text">{{ registerErrors.firstName }}</p>
              </div>
              <div>
                <label class="login-label">Last Name <span class="text-red-500">*</span></label>
                <input
                  type="text"
                  v-model="registerLastName"
                  @blur="validateRegisterLastName"
                  @input="clearRegisterError('lastName')"
                  required
                  placeholder="Doe"
                  :class="['login-input w-full', registerErrors.lastName ? 'login-input-error' : '']"
                >
                <p v-if="registerErrors.lastName" class="login-error-text">{{ registerErrors.lastName }}</p>
              </div>
            </div>

            <div>
              <label class="login-label">Email Address <span class="text-red-500">*</span></label>
              <input
                type="email"
                v-model="registerEmail"
                @blur="validateRegisterEmail"
                @input="clearRegisterError('email')"
                required
                placeholder="you@example.com"
                :class="['login-input w-full', registerErrors.email ? 'login-input-error' : '']"
              >
              <p v-if="registerErrors.email" class="login-error-text">{{ registerErrors.email }}</p>
            </div>

            <div>
              <label class="login-label">Password <span class="text-red-500">*</span></label>
              <div class="relative">
                <input
                  :type="showRegisterPassword ? 'text' : 'password'"
                  v-model="registerPassword"
                  @blur="validateRegisterPassword"
                  @input="clearRegisterError('password')"
                  required
                  placeholder="••••••••"
                  :class="['login-input w-full pr-12', registerErrors.password ? 'login-input-error' : '']"
                >
                <button
                  type="button"
                  @click="showRegisterPassword = !showRegisterPassword"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                  aria-label="Toggle password visibility"
                >
                  <svg v-if="showRegisterPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              <p v-if="registerErrors.password" class="login-error-text">{{ registerErrors.password }}</p>
              <p v-if="registerPassword && !registerErrors.password" class="mt-1.5 text-xs text-gray-500 font-medium">
                ✓ Minimum 8 characters with letters and numbers
              </p>
            </div>

            <div class="flex items-start gap-3 pt-2">
              <input type="checkbox" required class="login-checkbox mt-1 rounded border-gray-300 cursor-pointer flex-shrink-0">
              <label class="text-sm text-gray-600 leading-relaxed">
                I agree to the <a href="#" class="login-link font-semibold hover:underline cursor-pointer">terms and conditions</a> and <a href="#" class="login-link font-semibold hover:underline cursor-pointer">privacy policy</a>
              </label>
            </div>

            <div class="mt-4">
              <div ref="captchaWidgetRef" class="g-recaptcha"></div>
              <p v-if="captchaError" class="mt-2 text-xs text-red-600">{{ captchaError }}</p>
            </div>

            <div v-if="registerSuccess" class="login-message login-message-success mt-5">
              <svg class="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <p class="text-sm text-green-800 font-medium">Account created successfully! The email is verified after completing the captcha.</p>
            </div>

            <button
              type="submit"
              :disabled="isRegistering"
              class="login-submit-btn w-full py-3.5 text-sm font-semibold rounded-xl mt-6 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span v-if="isRegistering" class="flex items-center justify-center gap-2">
                <svg class="animate-spin h-5 w-5 text-[#1a1200]" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </span>
              <span v-else>Create Account</span>
            </button>

            <div v-if="registerError" class="login-message login-message-error mt-5">
              <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p class="text-sm text-red-800 font-medium">{{ registerError }}</p>
            </div>
          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { authService } from '@/services/authService'
import { checkRateLimit, recordFailedAttempt, clearRateLimit, getRateLimitInfo } from '@/utils/rateLimiter'
import { sanitizeAndValidateEmail, sanitizeName } from '@/utils/validation'

const router = useRouter()
const userStore = useUserStore()

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || import.meta.env.RECAPTCHA_SITE_KEY || ''

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const activeTab = ref<'login' | 'register'>('login')
const loginEmail = ref('')
const loginPassword = ref('')
const showLoginPassword = ref(false)
const rememberMe = ref(false)
const registerFirstName = ref('')
const registerLastName = ref('')
const registerEmail = ref('')
const registerPassword = ref('')
const showRegisterPassword = ref(false)
const captchaToken = ref('')
const captchaWidgetId = ref<number | null>(null)
const captchaWidgetRef = ref<HTMLDivElement | null>(null)
const captchaError = ref('')

// Keys para localStorage
const REMEMBER_ME_KEY = 'remembered_email'

// Cargar email guardado al abrir el modal y manejar scroll del body
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.style.overflow = 'hidden'
    captchaToken.value = ''
    captchaError.value = ''
    if (activeTab.value === 'login') {
      const rememberedEmail = localStorage.getItem(REMEMBER_ME_KEY)
      if (rememberedEmail) {
        loginEmail.value = rememberedEmail
        rememberMe.value = true
      }
    }
    if (activeTab.value === 'register') {
      captchaWidgetId.value = null
      renderRecaptchaWidget()
    }
  } else {
    document.body.style.overflow = ''
    captchaToken.value = ''
    captchaError.value = ''
    captchaWidgetId.value = null
  }
}, { immediate: true })

const renderRecaptchaWidget = async () => {
  captchaError.value = ''
  if (!RECAPTCHA_SITE_KEY) {
    captchaError.value = 'reCAPTCHA key is not configured.'
    return
  }

  try {
    const grecaptcha = await loadRecaptchaScript()
    await new Promise<void>((resolve) => grecaptcha.ready(resolve))
    if (captchaWidgetRef.value && captchaWidgetId.value === null) {
      captchaWidgetId.value = grecaptcha.render(captchaWidgetRef.value, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: (token: string) => {
          captchaToken.value = token
          captchaError.value = ''
        },
        'expired-callback': () => {
          captchaToken.value = ''
          captchaError.value = 'Captcha expired. Please complete it again.'
        },
        'error-callback': () => {
          captchaToken.value = ''
          captchaError.value = 'Captcha failed to load. Please refresh the page.'
        },
      })
    }
  } catch (err: any) {
    captchaError.value = err?.message || 'Unable to load reCAPTCHA widget.'
  }
}

watch(() => activeTab.value, (tab) => {
  if (tab === 'register' && props.isOpen) {
    captchaWidgetId.value = null
    captchaToken.value = ''
    captchaError.value = ''
    renderRecaptchaWidget()
  }
})

onMounted(() => {
  if (activeTab.value === 'register' && props.isOpen) {
    renderRecaptchaWidget()
  }
})

// Estados de validación y carga
const isLoggingIn = ref(false)
const isRegistering = ref(false)
const loginError = ref('')
const registerError = ref('')
const registerSuccess = ref(false)
const loginErrors = ref<{ email?: string; password?: string }>({})
const registerErrors = ref<{ firstName?: string; lastName?: string; email?: string; password?: string }>({})

// Validación de email
const validateEmail = (email: string): boolean => {
  const normalized = email.trim()
  if (!normalized || normalized.length > 254) return false
  if (normalized.includes(' ') || normalized.includes('..')) return false
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(normalized)
}

const loadRecaptchaScript = async () => {
  if (!RECAPTCHA_SITE_KEY) {
    throw new Error('reCAPTCHA site key is not configured.')
  }

  if ((window as any).grecaptcha) {
    return (window as any).grecaptcha
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="recaptcha/api.js"]') as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => {
        if ((window as any).grecaptcha) resolve((window as any).grecaptcha)
        else reject(new Error('reCAPTCHA loaded but grecaptcha is unavailable.'))
      })
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA script.')))
      return
    }

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => {
      if ((window as any).grecaptcha) resolve((window as any).grecaptcha)
      else reject(new Error('reCAPTCHA loaded but grecaptcha is unavailable.'))
    }
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA script.'))
    document.head.appendChild(script)
  })
}

const validateRecaptcha = (): boolean => {
  if (!captchaToken.value) {
    captchaError.value = 'Please complete the captcha challenge.'
    return false
  }
  captchaError.value = ''
  return true
}

// Validación de login
const validateLoginEmail = () => {
  if (!loginEmail.value) {
    loginErrors.value.email = 'Email is required'
    return false
  }
  if (!validateEmail(loginEmail.value)) {
    loginErrors.value.email = 'Please enter a valid email address'
    return false
  }
  loginErrors.value.email = undefined
  return true
}

const validateLoginPassword = () => {
  if (!loginPassword.value) {
    loginErrors.value.password = 'Password is required'
    return false
  }
  if (loginPassword.value.length < 6) {
    loginErrors.value.password = 'Password must be at least 6 characters'
    return false
  }
  loginErrors.value.password = undefined
  return true
}

const clearLoginError = (field: 'email' | 'password') => {
  loginErrors.value[field] = undefined
  loginError.value = ''
}

// Traducir mensajes de error de la API (si vienen en español) al inglés para la UI
const apiMessageToEnglish = (message: string): string => {
  const map: Record<string, string> = {
    'El usuario no existe.': 'User does not exist.',
    'Usuario no encontrado.': 'User not found.',
    'Credenciales inválidas.': 'Invalid credentials.',
    'Contraseña incorrecta.': 'Incorrect password.',
    'Email o contraseña incorrectos.': 'Invalid email or password.',
  }
  const trimmed = message?.trim() || ''
  return map[trimmed] ?? message
}

// Validación de registro
const validateRegisterFirstName = () => {
  if (!registerFirstName.value.trim()) {
    registerErrors.value.firstName = 'First name is required'
    return false
  }
  if (registerFirstName.value.trim().length < 2) {
    registerErrors.value.firstName = 'First name must be at least 2 characters'
    return false
  }
  if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(registerFirstName.value.trim())) {
    registerErrors.value.firstName = 'First name can only contain letters'
    return false
  }
  registerErrors.value.firstName = undefined
  return true
}

const validateRegisterLastName = () => {
  if (!registerLastName.value.trim()) {
    registerErrors.value.lastName = 'Last name is required'
    return false
  }
  if (registerLastName.value.trim().length < 2) {
    registerErrors.value.lastName = 'Last name must be at least 2 characters'
    return false
  }
  if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(registerLastName.value.trim())) {
    registerErrors.value.lastName = 'Last name can only contain letters'
    return false
  }
  registerErrors.value.lastName = undefined
  return true
}

const validateRegisterEmail = () => {
  if (!registerEmail.value.trim()) {
    registerErrors.value.email = 'Email is required'
    return false
  }
  if (!validateEmail(registerEmail.value.trim())) {
    registerErrors.value.email = 'Please enter a valid email address'
    return false
  }
  registerErrors.value.email = undefined
  return true
}

const validateRegisterPassword = () => {
  if (!registerPassword.value) {
    registerErrors.value.password = 'Password is required'
    return false
  }
  if (registerPassword.value.length < 8) {
    registerErrors.value.password = 'Password must be at least 8 characters'
    return false
  }
  // Validar que tenga al menos una letra y un número
  const hasLetter = /[a-zA-Z]/.test(registerPassword.value)
  const hasNumber = /[0-9]/.test(registerPassword.value)
  if (!hasLetter || !hasNumber) {
    registerErrors.value.password = 'Password must contain letters and numbers'
    return false
  }
  registerErrors.value.password = undefined
  return true
}

const clearRegisterError = (field: 'firstName' | 'lastName' | 'email' | 'password') => {
  registerErrors.value[field] = undefined
  registerError.value = ''
}

const close = () => {
  emit('close')
  document.body.style.overflow = ''
  // Resetear formularios (pero mantener email si remember me está activo)
  if (!rememberMe.value) {
    loginEmail.value = ''
  }
  loginPassword.value = ''
  showLoginPassword.value = false
  registerFirstName.value = ''
  registerLastName.value = ''
  registerEmail.value = ''
  registerPassword.value = ''
  showRegisterPassword.value = false
  loginErrors.value = {}
  registerErrors.value = {}
  loginError.value = ''
  registerError.value = ''
  registerSuccess.value = false
  isLoggingIn.value = false
  isRegistering.value = false
}

const goToForgotPassword = () => {
  close()
  router.push({ name: 'forgot-password' })
}

const handleLogin = async () => {
  // Validar campos
  const isEmailValid = validateLoginEmail()
  const isPasswordValid = validateLoginPassword()
  
  if (!isEmailValid || !isPasswordValid) {
    return
  }

  // Sanitizar email
  const emailValidation = sanitizeAndValidateEmail(loginEmail.value)
  if (!emailValidation.valid) {
    loginError.value = emailValidation.error || 'Invalid email format'
    return
  }

  // Verificar rate limiting
  const rateLimitKey = `login:${emailValidation.sanitized}`
  const rateLimitCheck = checkRateLimit(rateLimitKey)
  
  if (!rateLimitCheck.allowed) {
    const minutesRemaining = Math.ceil((rateLimitCheck.resetTime! - Date.now()) / 60000)
    loginError.value = `Too many login attempts. Please try again in ${minutesRemaining} minute(s).`
    isLoggingIn.value = false
    return
  }

  isLoggingIn.value = true
  loginError.value = ''

  try {
    // Llamada real a la API con email sanitizado
    const response = await authService.signIn({
      email: emailValidation.sanitized,
      password: loginPassword.value, // La contraseña no se sanitiza, se envía tal cual
    })
    
    // Limpiar rate limit en caso de éxito
    clearRateLimit(rateLimitKey)
    
    // Guardar email en localStorage si "Remember me" está marcado
    if (rememberMe.value) {
      localStorage.setItem(REMEMBER_ME_KEY, emailValidation.sanitized)
    } else {
      // Si no está marcado, eliminar el email guardado si existe
      localStorage.removeItem(REMEMBER_ME_KEY)
    }
    
    // Guardar usuario en el store
    userStore.login(response)
    
    // Verificar si hay una ruta de redirección en el query
    const redirectPath = router.currentRoute.value.query.redirect as string
    
    close()
    
    // Redirigir después de cerrar el modal
    if (redirectPath) {
      router.push(redirectPath)
    }
  } catch (error: any) {
    // Registrar intento fallido
    recordFailedAttempt(rateLimitKey)
    
    // Prevenir cualquier comportamiento por defecto que pueda causar reload
    if (error?.preventDefault) {
      error.preventDefault()
    }
    
    // Extraer el mensaje de error
    let errorMessage = 'Error signing in. Please try again.'
    
    if (error.response) {
      // El servidor respondió con un error (traducir al inglés si la API devuelve español)
      const raw = error.response.data?.message ||
                  error.response.data?.error ||
                  `Error: ${error.response.status} ${error.response.statusText}` ||
                  'Invalid email or password'
      errorMessage = apiMessageToEnglish(raw)
    } else if (error.message) {
      errorMessage = apiMessageToEnglish(error.message)
    }
    
    // Mostrar información de rate limit si está cerca del límite
    const rateLimitInfo = getRateLimitInfo(rateLimitKey)
    if (rateLimitInfo.count >= 3) {
      const remaining = 5 - rateLimitInfo.count
      errorMessage += ` (${remaining} attempt(s) remaining)`
    }
    
    loginError.value = errorMessage
  } finally {
    isLoggingIn.value = false
  }
}

const handleRegister = async () => {
  // Validar campos
  const isFirstNameValid = validateRegisterFirstName()
  const isLastNameValid = validateRegisterLastName()
  const isEmailValid = validateRegisterEmail()
  const isPasswordValid = validateRegisterPassword()
  
  if (!isFirstNameValid || !isLastNameValid || !isEmailValid || !isPasswordValid) {
    return
  }

  // Sanitizar inputs
  const sanitizedFirstName = sanitizeName(registerFirstName.value)
  const sanitizedLastName = sanitizeName(registerLastName.value)
  const emailValidation = sanitizeAndValidateEmail(registerEmail.value)
  
  if (!emailValidation.valid) {
    registerError.value = emailValidation.error || 'Invalid email format'
    return
  }

  if (!validateRecaptcha()) {
    return
  }

  isRegistering.value = true
  registerError.value = ''
  registerSuccess.value = false

  try {
    // Antes de ejecutar reCAPTCHA, verificar si el email ya existe en la base de datos
    try {
      const look = await authService.checkEmailExists(emailValidation.sanitized)
      const exists = !!(look && (look.exists === true || look.found === true || look === true))
      if (exists) {
        registerError.value = 'An account with this email already exists.'
        isRegistering.value = false
        return
      }
    } catch (e: any) {
      // Manejar 400/errores del endpoint look-email
      const msg = e?.response?.data?.message || e?.message || 'Failed to validate email availability.'
      registerError.value = apiMessageToEnglish(msg)
      isRegistering.value = false
      return
    }

    // Usar el token del widget visible de reCAPTCHA.
    const signupPayload = {
      First_name: sanitizedFirstName,
      Last_name: sanitizedLastName,
      Email: emailValidation.sanitized,
      Password: registerPassword.value, // La contraseña no se sanitiza
      recaptchaToken: captchaToken.value,
      is_email_verified: true,
    }
    console.log('SignUp payload:', signupPayload)

    // Llamada real a la API con datos sanitizados
    await authService.signUp(signupPayload)
    
    // Guardar el email antes de limpiar para prellenarlo en el login
    const registeredEmail = registerEmail.value.trim()
    
    // Mostrar mensaje de éxito
    registerSuccess.value = true
    registerError.value = ''
    
    // Limpiar el formulario de registro
    registerFirstName.value = ''
    registerLastName.value = ''
    registerEmail.value = ''
    registerPassword.value = ''
    registerErrors.value = {}
    
    // Cambiar a la pestaña de login después de un breve delay
    setTimeout(() => {
      activeTab.value = 'login'
      registerSuccess.value = false
      // Pre-llenar el email en el login
      loginEmail.value = registeredEmail
    }, 1500)
    
  } catch (error: any) {
    registerSuccess.value = false
    // Mostrar detalles en consola para depuración
    console.error('Signup error:', error?.response?.status, error?.response?.data || error?.message)

    const raw = error.response?.data?.message || error.response?.data || error.message || 'Error creating account. Please try again.'
    // Si el servidor devolvió objeto con detalles, stringifylo para mostrar algo útil
    const display = typeof raw === 'object' ? JSON.stringify(raw) : String(raw)
    registerError.value = apiMessageToEnglish(display)
  } finally {
    isRegistering.value = false
  }
}

</script>

<style scoped>
/* Overlay con blur premium */
.login-overlay-backdrop {
  background: rgba(15, 15, 15, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.login-overlay-enter-active,
.login-overlay-leave-active {
  transition: opacity 0.2s ease;
}
.login-overlay-enter-from,
.login-overlay-leave-to {
  opacity: 0;
}

/* Modal: sombras en capas, borde sutil */
.login-modal-box {
  background: #fff;
  border-radius: 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.03),
    0 2px 4px rgba(0, 0, 0, 0.04),
    0 12px 24px rgba(0, 0, 0, 0.08),
    0 24px 48px rgba(0, 0, 0, 0.06);
}
.login-modal-enter-active,
.login-modal-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.login-modal-enter-from,
.login-modal-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.98);
}

/* Cerrar: discreto, hover suave */
.login-close-btn {
  background: transparent;
}
.login-close-btn:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Tabs premium */
.login-tabs {
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.login-tab-inactive {
  color: #6b7280;
}
.login-tab-inactive:hover {
  color: #374151;
  background: rgba(255, 255, 255, 0.8);
}
.login-tab-active {
  color: #1a1200;
  background: #EDD97A;
  box-shadow: 0 1px 3px rgba(237, 217, 122, 0.45);
}

/* Labels */
.login-label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.01em;
  margin-bottom: 0.5rem;
}

/* Inputs premium */
.login-input {
  padding: 0.625rem 1rem;
  font-size: 0.9375rem;
  color: #111;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.login-input::placeholder {
  color: #9ca3af;
}
.login-input:hover:not(:focus):not(.login-input-error) {
  border-color: #d1d5db;
  background: #fff;
}
.login-input:focus {
  border-color: #5f4842;
  box-shadow: 0 0 0 3px rgba(95, 72, 66, 0.12);
  background: #fff;
}
.login-input-error {
  border-color: #f87171 !important;
  background: #fef2f2 !important;
}
.login-input-error:focus {
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.2);
}

.login-error-text {
  margin-top: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #dc2626;
}

/* Checkbox con color secundario */
.login-checkbox {
  width: 1rem;
  height: 1rem;
  accent-color: #5f4842;
}

/* Enlaces secundarios */
.login-link {
  color: #5f4842;
}

/* Botón principal premium */
.login-submit-btn {
  color: #1a1200;
  background: #EDD97A;
  box-shadow: 0 2px 8px rgba(237, 217, 122, 0.35);
  transition: box-shadow 0.2s ease, transform 0.15s ease, background 0.2s ease;
}
.login-submit-btn:hover:not(:disabled) {
  background: #c9a94f;
  box-shadow: 0 4px 14px rgba(237, 217, 122, 0.5);
  transform: translateY(-1px);
}
.login-submit-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(237, 217, 122, 0.35);
}
.login-submit-btn:disabled {
  background: #d4be5e;
}

/* Mensajes de estado */
.login-message {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
}
.login-message-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
}
.login-message-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}
</style>

